/**
 * Das eine Angebot in „Nebenbei" — wechselt mit der Tageszeit. (24.09.2026,
 * Ticket 2561)
 *
 * Vorher stand die Morgenmeditation (Ticket 2549) den ganzen Tag ganz oben in
 * „Heute", mit gefülltem „Anhören" — sie wirkte wie eine Aufgabe. Der
 * freigegebene Entwurf („Der Entwurf ist sehr schön.") legt sie als EINE
 * leise Stelle an den Anfang von „Nebenbei", und diese Stelle wechselt:
 *
 *   morgens   (vor 11:00)  die Morgenmeditation, sobald die Datei fertig ist
 *   tagsüber  (11–17 Uhr)  der Fund des Tages, mit dem Weg in den Rückblick
 *   abends    (ab 17:00)   „Wofür warst du heute dankbar?"
 *
 * ⚠️ Gerechnet in Europe/Vienna — nicht in der Zeit des Geräts und nicht in
 *    UTC. Ein Telefon auf Reisen oder ein Rechner auf UTC sähe sonst um
 *    10 Uhr Wiener Zeit schon den Nachmittag.
 * ⚠️ Nie mehr als EIN Angebot, nie ein Platzhalter. Fällt eines aus, gilt
 *    das nächste passende — oder die Stelle bleibt weg.
 * ⚠️ Rein: kein DOM, kein fetch. zuhause-view.ts zeichnet nur, was hier
 *    entschieden wird — EIN Besitzer für die Stelle.
 */

export type Tagesphase = "morgens" | "tagsueber" | "abends";

/** Ab dieser Wiener Stunde ist es tagsüber (dieselbe Grenze wie SPAETESTENS_STUNDE der Meditation). */
const GRENZE_TAGSUEBER = 11;
/** Ab dieser Wiener Stunde ist es abends. */
const GRENZE_ABENDS = 17;

/**
 * Ein angefangener Player wird an der Grenze nicht abgerissen: die Stelle
 * bleibt bei der Meditation, bis sie samt dieser Nachfrist durch sein könnte.
 */
export const NACHFRIST_MS = 15 * 60_000;

/** Was /api/morgenmeditation liefert. */
interface MeditationStand {
  bereit?: boolean;
  tag?: string;
  handlung?: string | null;
  dauerSek?: number | null;
}

/** Was von /api/fund/heute gebraucht wird. */
interface FundStand {
  titel: string;
  text: string;
  /** Tagesschlüssel des Funds (YYYY-MM-DD) — fehlt er, gilt er als heutiger. */
  tag?: string;
}

export interface AngebotDaten {
  meditation: MeditationStand | null;
  fund: FundStand | null;
  /** Wann „Anhören" getippt wurde — hält die Meditation über die Grenze. */
  gestartet?: { tag: string; um: number; dauerSek: number | null } | null;
}

type AngebotArt = "meditation" | "fund" | "dankbar";

export interface Angebot {
  art: AngebotArt;
  /** Gleicher Schlüssel = gleiches Bild; die Ansicht baut dann nichts neu (der Player bleibt). */
  schluessel: string;
  /** Name im Zeichen-Vorrat (lib/zeichen.ts) für das Zeichen neben „Nebenbei". */
  zeichen: string;
  /**
   * Die Tageszeit des Angebots — sie steht AN der Überschrift „Nebenbei",
   * nicht als eigene Zeile im Kasten. (29.09.2026, Ticket 2561)
   *
   * Martin, morgens am Telefon: unter „Nebenbei" stehe nur „Nebenbei", die
   * Meditation („für dich heute früh") weiter unten. Gemessen: Die Stelle
   * war im DOM das erste Kind der Fläche (kein CSS-`order`, kein Raster
   * dazwischen) — aber ihre erste Zeile „FÜR DICH · HEUTE FRÜH" trug genau
   * den Stil der Überschrift (11 px, Versalien, 600, gedämpft). Das Auge las
   * zwei Überschriften übereinander und „Morgenmeditation" erst als dritte.
   */
  kopf: string;
  titel: string;
  satz: string;
  knopf: string;
  /** Zeichen im Knopf; `vorn` steht es vor dem Wort, sonst dahinter (Pfeil). */
  knopfZeichen: { name: string; vorn: boolean };
  /** Nur bei der Meditation: der Tag, den der Player abspielt. */
  tag?: string;
}

interface NebenbeiWahl {
  angebot: Angebot | null;
  /**
   * Der Titel für den leisen Fund-Hinweis — oder null. Steht der Fund schon
   * als Angebot da, fällt der Hinweis weg: keine Dopplung.
   */
  fundHinweis: string | null;
}

/** Die Frage am Abend — EINE Stelle für Anzeige und Gespräch. */
export const DANKBAR_FRAGE = "Wofür warst du heute dankbar?";

/**
 * Was „Erzählen" ins Gespräch schickt.
 *
 * ⚠️ MIT Herkunft (wie der Impuls aus dem Spiegel und die Meditation seit
 *    23.09.): nackt ginge die Frage als Martins eigene Aussage hinaus, und
 *    die Auswertung legte daraus eine Erinnerung über ihn an.
 * ⚠️ Kein Wort von einem Tagebuch: Die App hat keines (gesucht 24.09. in
 *    frontend, server, tools). Was er erzählt, liegt im Gespräch — mehr
 *    verspricht der Satz nicht.
 */
export const DANKBAR_NACHRICHT =
  `Aus dem Zuhause, die Frage zum Ausklang: „${DANKBAR_FRAGE}“ — ` +
  "frag mich danach, ich erzähl dir ein paar Dinge.";

const WIEN_UHR = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Vienna",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** Minute des Tages in Wiener Zeit (0 … 1439). */
export function wienerMinute(jetzt: Date): number {
  const teile = WIEN_UHR.formatToParts(jetzt);
  const h = Number(teile.find((t) => t.type === "hour")?.value ?? "9") % 24;
  const m = Number(teile.find((t) => t.type === "minute")?.value ?? "0");
  return h * 60 + m;
}

/** Tagesschlüssel (YYYY-MM-DD) in Wiener Zeit. */
export function wienerTag(jetzt: Date): string {
  return jetzt.toLocaleDateString("en-CA", { timeZone: "Europe/Vienna" });
}

export function tagesphase(jetzt: Date): Tagesphase {
  const min = wienerMinute(jetzt);
  if (min < GRENZE_TAGSUEBER * 60) return "morgens";
  if (min < GRENZE_ABENDS * 60) return "tagsueber";
  return "abends";
}

/**
 * Der erste Satz eines Markdown-Texts, höchstens `max` Zeichen — an einer
 * Wortgrenze gekürzt, mit „…". Überschriften und Auszeichnung fallen weg.
 */
export function ersterSatz(text: string, max = 160): string {
  const fliess = text
    .split("\n")
    .filter((z) => !/^\s*#/.test(z))
    .join(" ")
    .replace(/[*_`>]+/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (!fliess) return "";
  const satz = bisSatzende(fliess);
  if (satz.length <= max) return satz;
  const schnitt = satz.slice(0, max - 1);
  const wort = schnitt.lastIndexOf(" ");
  return `${(wort > max / 2 ? schnitt.slice(0, wort) : schnitt).replace(/[\s,;:–—-]+$/, "")}…`;
}

/**
 * Kein Satzende: ein Punkt nach einer Ziffer („am 14.09.“, „am 14. September“),
 * nach einem einzelnen Buchstaben („z. B.“) oder nach einer gängigen Abkürzung.
 * (24.09.2026, Prüfbefund zu 2561) Der Fund-Auftrag verlangt ausdrücklich
 * Daten — mit dem alten Muster endeten 6 von 28 echten Funden nach 2 bis 4
 * Wörtern, alle vom 22. und 24.09.
 */
const KEIN_SATZENDE_DAVOR =
  /(?:\d|(?:^|[\s(„"])\p{L}|\b(?:ca|bzw|usw|etc|vgl|Nr|Dr|Hr|Fr|St|evtl|ggf|inkl|ggü|Std|Min|Mio|Tsd|u\.a|d\.h))$/u;

/** Der Text bis zum ersten echten Satzende, samt schließendem Anführungszeichen. */
function bisSatzende(text: string): string {
  const zeichen = /[.!?…]["“”»]?/g;
  for (let m = zeichen.exec(text); m; m = zeichen.exec(text)) {
    const danach = text.slice(m.index + m[0].length);
    // Danach muss der Text enden oder ein neuer Satz beginnen (Großbuchstabe, Anführung, Klammer).
    if (danach.trim() !== "" && !/^\s+[\p{Lu}„"«»(]/u.test(danach)) continue;
    if (m[0][0] === "." && KEIN_SATZENDE_DAVOR.test(text.slice(0, m.index))) continue;
    return text.slice(0, m.index + m[0].length);
  }
  return text;
}

function minutenWort(dauerSek: number | null | undefined): string {
  if (!dauerSek || dauerSek <= 0) return "";
  const n = Math.max(1, Math.round(dauerSek / 60));
  return n === 1 ? "1 Minute" : `${n} Minuten`;
}

/**
 * Der Schritt ohne führendes „Heute“ — sonst stünde „Dein Schritt heute: Heute
 * einmal …“ da (Prüfbefund 24.09.). Was danach kommt, bleibt, wie es ist.
 */
export function schrittOhneHeute(handlung: string): string {
  return handlung.replace(/^\s*heute\s*,?\s+/i, "");
}

function meditationAngebot(m: MeditationStand & { tag: string }): Angebot {
  const handlung = schrittOhneHeute(m.handlung?.trim() ?? "");
  const dauer = minutenWort(m.dauerSek);
  return {
    art: "meditation",
    schluessel: `meditation:${m.tag}`,
    zeichen: "stern",
    kopf: "heute früh",
    titel: "Morgenmeditation",
    satz: handlung
      ? `Aus deinem gestrigen Tag. Dein Schritt heute: ${handlung}`
      : "Aus deinem gestrigen Tag.",
    knopf: dauer ? `Anhören · ${dauer}` : "Anhören",
    knopfZeichen: { name: "abspielen", vorn: true },
    tag: m.tag,
  };
}

function fundAngebot(f: FundStand): Angebot {
  return {
    art: "fund",
    schluessel: `fund:${f.tag ?? ""}:${f.titel}`,
    zeichen: "dokument",
    kopf: "Fund des Tages",
    titel: f.titel,
    satz: ersterSatz(f.text),
    knopf: "Im Rückblick lesen",
    knopfZeichen: { name: "pfeil-rechts", vorn: false },
  };
}

const DANKBAR: Angebot = {
  art: "dankbar",
  schluessel: "dankbar",
  zeichen: "mond",
  kopf: "zum Ausklang",
  titel: DANKBAR_FRAGE,
  satz: "Drei Dinge genügen. Erzähl sie Vespera.",
  knopf: "Erzählen",
  knopfZeichen: { name: "gespraech", vorn: true },
};

/**
 * Liegt die Meditation dieses Stands HEUTE anhörbar bereit?
 *
 * ⚠️ EINE Fassung für das Angebot hier und den Weg „Meditation" auf der Tafel
 *    (zuhause-view.ts, 27.09.2026, Ticket 2636) — eine Meditation von gestern
 *    gilt nicht.
 */
export function meditationHeute(
  jetzt: Date,
  m: MeditationStand | null | undefined,
): m is MeditationStand & { tag: string } {
  return Boolean(m?.bereit) && m?.tag === wienerTag(jetzt);
}

/**
 * Welches Angebot jetzt in „Nebenbei" steht — und ob der Fund-Hinweis bleibt.
 *
 *   morgens:  Meditation → sonst der Fund (das nächste passende) → sonst nichts
 *   tagsüber: Fund → sonst nichts (die Meditation um 14 Uhr wäre wieder
 *             die Aufgabe, die den ganzen Tag dasteht)
 *   abends:   die Dankbarkeitsfrage — sie braucht keine Daten
 *
 * Eine Meditation oder ein Fund von einem anderen Tag gilt nicht.
 */
export function waehleNebenbei(jetzt: Date, daten: AngebotDaten): NebenbeiWahl {
  const heute = wienerTag(jetzt);
  const m = daten.meditation;
  const meditation = meditationHeute(jetzt, m) ? meditationAngebot(m) : null;
  const f = daten.fund;
  const fund =
    f?.titel?.trim() && f.text?.trim() && (!f.tag || f.tag === heute) ? f : null;

  const g = daten.gestartet;
  const laeuftNoch =
    meditation !== null &&
    g?.tag === heute &&
    jetzt.getTime() < g.um + (g.dauerSek ?? 0) * 1000 + NACHFRIST_MS;

  let angebot: Angebot | null;
  if (laeuftNoch) {
    angebot = meditation;
  } else {
    switch (tagesphase(jetzt)) {
      case "morgens":
        angebot = meditation ?? (fund ? fundAngebot(fund) : null);
        break;
      case "tagsueber":
        angebot = fund ? fundAngebot(fund) : null;
        break;
      default:
        angebot = DANKBAR;
    }
  }
  return {
    angebot,
    fundHinweis: fund && angebot?.art !== "fund" ? fund.titel : null,
  };
}
